import { billingService } from 'v2/shared/services/billing-service'
import { formatCurrency } from 'v2/shared/utils'
import { Currency } from './billing.config'
import { getCashfree } from './cashfree-sdk'

// Shared helpers for the two recharge modals (RechargeModal and
// CashfreeRechargeModal) — single source of truth so their payment logic can't
// drift apart.

// The minimum recharge each INR bank rail settles.
//
// Both are 10,000 today, and they are still TWO named constants because the
// numbers are independent facts that merely coincide:
//
//   netbanking     — Cashfree's merchant-account limit. SER-5416 (989daee31a):
//                    "Cashfree netbanking on the Plivo merchant account is only
//                    enabled for transactions of 10,000 and above."
//   bank transfer  — the virtual-account settlement rule.
//
// Collapsing them into one number means lifting Cashfree's limit would silently
// move the amount the bank-transfer drawer tells customers to wire.
//
// There is deliberately NO gateway dimension. Netbanking always runs on Cashfree
// — Razorpay does not support it (see useCashfreeNetbankingCheckout, which every
// netbanking entry point routes through) — so a gateway parameter here could
// never change the answer, and a parameter that cannot change an answer is one
// more thing a caller can get wrong.
export type BankRail = 'netbanking' | 'bank_transfer'

export const INR_NETBANKING_MIN_AMOUNT = 10_000
export const INR_BANK_TRANSFER_MIN_AMOUNT = 10_000

export const getBankRailMinAmount = (rail: BankRail): number =>
  rail === 'netbanking' ? INR_NETBANKING_MIN_AMOUNT : INR_BANK_TRANSFER_MIN_AMOUNT

// Netbanking / bank transfer carry no gateway fee.
export const ZERO_FEE_LABEL = 'Fee: 0%'

// Disabled-reason string when `rail` can't be used for `amount` (below that
// rail's INR floor), else undefined. One code path and one message shape for both
// rails, so only the number differs. The internal-QA flag `isCashFreeTesting`
// bypasses the floor on both gateways.
export const getBankRailDisabledReason = (
  rail: BankRail,
  amount: number,
  currency: string,
  bypassFloor: boolean,
): string | undefined => {
  const floor = getBankRailMinAmount(rail)
  return !bypassFloor && currency === Currency.INR && amount > 0 && amount < floor
    ? `Available for ${formatCurrency(floor, currency, false)} or more`
    : undefined
}

// Netbanking recharge choices with sub-floor amounts stripped out. The
// netbanking-only screen shouldn't offer tiles it would immediately reject (vs.
// just disabling the CTA). QA bypass + non-INR are left untouched, mirroring
// getBankRailDisabledReason.
export const getNetbankingRechargeChoices = (
  choices: number[] | undefined,
  currency: string,
  bypassFloor: boolean,
): number[] | undefined =>
  choices?.filter(
    (amount) =>
      bypassFloor || currency !== Currency.INR || amount >= getBankRailMinAmount('netbanking'),
  )

// Card gateway-fee badge, e.g. "Fee: 2%". Undefined when the rate is unknown.
export const getCardFeeLabel = (gatewayFeeRate?: number): string | undefined =>
  gatewayFeeRate !== undefined ? `Fee: ${+gatewayFeeRate.toFixed(2)}%` : undefined

// Spread before sorting — Array.prototype.sort mutates in place and
// recharge_choices is the React Query cache reference. Returns the choices
// ascending, or undefined when none are available.
export const getSortedRechargeChoices = (choices?: number[]): number[] | undefined =>
  choices ? [...choices].sort((a, b) => a - b) : undefined

// Pull the human-readable message payment-service returns out of an axios error.
// The error body is structs.Response — `message` on most endpoints, `error` on a
// few — so surfacing it gives the user the real reason ("Only INR…", "Amount must
// be between…") instead of a generic failure. Falls back to the caller's default.
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response
    ?.data
  return data?.message || data?.error || fallback
}

// Where Cashfree's hosted checkout redirects back to after a card / netbanking
// payment; `method` drives the post-redirect callback messaging.
export const buildPaymentCallbackUrl = (method: string): string =>
  `${window.location.origin}/payment/callback?method=${method}`

// Hand the order's payment_session_id to the Cashfree.js SDK. By default the SDK
// redirects the current tab to the hosted checkout page (returns via
// /payment/callback). When `useModal` is true (embedded-checkout flag) it opens
// an in-page modal and the promise resolves once the modal closes — the caller
// must then confirm the outcome via `verifyAndPollCashfreePayment`.
export const launchCashfreeHostedCheckout = async (
  paymentSessionId: string,
  useModal = false,
): Promise<void> => {
  const cashfree = await getCashfree()
  await cashfree.checkout({
    paymentSessionId,
    redirectTarget: useModal ? '_modal' : '_self',
  })
}

// Cashfree status vocabularies differ by endpoint: verify (POST) returns the raw
// OrderStatus (PAID/ACTIVE/EXPIRED/TERMINATED), get-status (GET) returns the
// DB-mapped status (SUCCESS/PENDING/FAILED/USER_DROPPED/CANCELLED/VOID). Treat
// both "paid" spellings as success.
const CASHFREE_PAID_STATUSES = new Set(['PAID', 'SUCCESS'])
const CASHFREE_TERMINAL_STATUSES = new Set([
  'PAID',
  'SUCCESS',
  'FAILED',
  'EXPIRED',
  'TERMINATED',
  'USER_DROPPED',
  'CANCELLED',
  'VOID',
  // get-status can return NOT_ATTEMPTED when the modal is closed before paying —
  // terminal (not paid) so we exit fast instead of polling into a false "processing".
  'NOT_ATTEMPTED',
])

export const isCashfreePaid = (status: string): boolean =>
  CASHFREE_PAID_STATUSES.has(status.toUpperCase())

// The customer dismissed the hosted modal without completing payment — terminal,
// but distinct from a genuine gateway failure, so callers stay quiet instead of
// shouting "Payment Failed" at someone who chose to back out.
const CASHFREE_CANCELLED_STATUSES = new Set(['USER_DROPPED', 'NOT_ATTEMPTED', 'CANCELLED'])

export const isCashfreeUserCancelled = (status: string): boolean =>
  CASHFREE_CANCELLED_STATUSES.has(status.toUpperCase())

const isCashfreeTerminal = (status: string): boolean =>
  CASHFREE_TERMINAL_STATUSES.has(status.toUpperCase())

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

// After the hosted modal closes, confirm the real outcome server-side: verify
// once (triggers the saved-card sync + status refresh), then poll get-status
// until a terminal state. Returns the final payment_status, or 'PENDING' if it
// hasn't settled within the window (the gateway webhook reconciles shortly after).
export const verifyAndPollCashfreePayment = async (orderId: string): Promise<string> => {
  try {
    const verified = await billingService.cashfreeVerifyPayment(orderId)
    if (isCashfreeTerminal(verified.payment_status)) return verified.payment_status
  } catch {
    // fall through to polling
  }
  for (let attempt = 0; attempt < 8; attempt++) {
    await delay(2000)
    try {
      const status = await billingService.cashfreeGetPaymentStatus(orderId)
      if (isCashfreeTerminal(status.payment_status)) return status.payment_status
    } catch {
      // transient error — keep polling
    }
  }
  return 'PENDING'
}
